/*
 * COPYRIGHT MOTOROLA SOLUTIONS, INC.
 * ALL RIGHTS RESERVED.
 * MOTOROLA SOLUTIONS CONFIDENTIAL RESTRICTED
 */

import { Injectable } from '@angular/core';
import { HealthService } from './health.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EnvironmentService } from './environment.service';
import { HotKey } from '../model/hotkey';
import { LayoutSettings, Role } from '../../configuration/model/layout-settings';
import { UiSettings } from '../../configuration/model/ui-settings';
import { CallAlert } from '../../call/model/audible-alert';
import { ExternalApplication } from '../../main/application-links/model/external-application';
import { CallQueue, CallQueueConfigFormData, QueueACD } from 'CalltakingCoreApi';
import { map } from 'rxjs/operators';
import { Contact } from '../../directory/model/contact';
import { Folder } from '../../directory/model/folder';
import { SpeedDial } from '../../directory/model/speed-dial';
import { ServiceName } from '../../../assets/config/environment';
import { ServiceResponders } from '../../location/model/service-responders';
import { LocationLayoutResponse } from '../../location/model/location-item-template';
import { PredefinedMessages } from '../../messages/model/pre-defined-messages';
import {
    AbandonedCallsDistributionSettings,
    CallListConfiguration,
    SupervisorAgencySettings,
    UserSettings
} from '../../settings/model/settings.model';
import { ConsolePosition } from '../../cad-config/model/console-position';
import { EMPTY } from 'rxjs';
import { Ringdown } from '../../directory/directory-ringdowns-table/Ringdown';
import { AcdStatusMessage, AcdStatusMessageContentUpdateDto, WallboardTemplate } from '../../wallboard/model/WallboardTemplate';
import { CustomHttpParamEncoder } from '../util/custom-http-param-encoder';
import { UILogStreamingConfig } from '../../configuration/model/ui-log-streaming-config';

@Injectable({ providedIn: 'root' })
export class SkipperService extends HealthService {
    private readonly HOTKEY_URL = `/api/config-mgmt/v1/hotkeys/`;
    private readonly ALERTS_URI = '/api/config-mgmt/v1/call-alerts/';
    private readonly UI_CONFIG_URL = '/api/config-mgmt/v1/console-ui/settings';
    private readonly LANGUAGES_URL = '/api/config-mgmt/v1/roles/preferred/languages';
    private readonly APPLICATION_LINKS_URL = '/api/config-mgmt/v1/settings/applications';
    private readonly CONSOLE_LAYOUT_URL = '/api/config-mgmt/v1/settings/layouts/preferred-role';
    private readonly QUEUE_CONFIG = '/api/config-mgmt/v1/call-queue';
    private readonly CONTACTS_API_URL = '/api/config-mgmt/v1/directory';
    private readonly CONTACTS_URL = `${this.CONTACTS_API_URL}/contacts`;
    private readonly SPEED_DIAL_FOLDER_URL = `${this.CONTACTS_API_URL}/speed-dial/display-order`;
    private readonly LOCATION_LAYOUT_URL = `/api/config-mgmt/v1/location-layout/`;
    private readonly SERVICE_RESPONDERS_URL = `/api/config-mgmt/v1/service-responders/`;
    private readonly PREDEFINED_SMS_MESSAGES = '/api/config-mgmt/v1/messages/sms';
    private readonly PREDEFINED_TTY_MESSAGES = '/api/config-mgmt/v1/messages/tty';
    private readonly USER_PREFIX = '/api/config-mgmt/v1/users';
    private readonly CURRENT_ROLE_SUFFIX = 'current-role';
    private readonly USER_SELECTABLE_ROLES_ENDPOINT = '/api/config-mgmt/v1/roles/role-users/selectable-roles';
    private readonly SUPERVISED_ROLES_ENDPOINT = '/api/config-mgmt/v1/roles/role-users/supervised-roles';
    private readonly USER_SETTINGS_ENDPOINT = '/api/config-mgmt/v1/user-ui-settings';
    private readonly AGENCY_SETTINGS_ENDPOINT = '/api/config-mgmt/v1/settings/supervisor-views/preferred-role';
    private readonly CALL_LIST_ENDPOINT = '/api/config-mgmt/v1/call-lists/preferred-role';
    private readonly VERSION_ENDPOINT = '/api/config-mgmt/v1/about/version';
    private readonly POSITIONS_ENDPOINT = '/api/config-mgmt/v1/positions';
    private readonly SPEED_DIAL_LAYOUTS_URL = '/api/config-mgmt/v1/directory/speed-dial/esn-suggested';
    private readonly SPEED_DIAL_CONTACTS_URL = '/api/config-mgmt/v1/directory/speed-dial/contact';
    private readonly RINGDOWNS_URL = '/api/config-mgmt/v1/ringdowns/dictionary';
    private readonly ABANDONED_CALLS_CONFIGURATION_URL = '/api/config-mgmt/v1/abandoned-call-routing';
    private readonly ROLE_BY_NAME_ENDPOINT = '/api/config-mgmt/v1/roles/name';
    public readonly TEST_POSITION_CONFIG_URL = 'assets/data/position-test-config.json';
    private readonly UPDATE_WALLBOARD_TEMPLATES_WITH_MESSAGES_URL = '/api/config-mgmt/v1/acd-status/messages/content';
    private readonly FETCH_WALLBOARD_TEMPLATES_WITH_MESSAGES_URL = '/api/config-mgmt/v1/acd-status/templates/limited/messages';
    private readonly TRANSLATION_SETTINGS_URL = '/api/config-mgmt/v1/translation-settings';
    private readonly UI_LOGGING_CONFIGS_URL = '/api/config-mgmt/v1/ui-logging-configs';

    constructor(
        protected http: HttpClient,
        protected env: EnvironmentService
    ) {
        super(http, ServiceName.SKIPPER, env.environment.SERVICES[ServiceName.SKIPPER]);
    }

    public requestContacts() {
        return this.http.get<Contact[]>(`${this.activeUrl$.value}${this.CONTACTS_URL}`, {
            observe: 'body',
            responseType: 'json'
        });
    }

    public requestDirectoryEntries() {
        return this.http.get<(Folder | SpeedDial)[]>(`${this.activeUrl$.value}${this.SPEED_DIAL_FOLDER_URL}`, {
            observe: 'body',
            responseType: 'json'
        });
    }

    public requestSpeedDialLayouts() {
        return this.http.get<Record<string, SpeedDial[]>>(`${this.activeUrl$.value}${this.SPEED_DIAL_LAYOUTS_URL}`, {
            observe: 'body',
            responseType: 'json'
        });
    }

    public requestHotkeys() {
        return this.http.get<HotKey[]>(`${this.activeUrl$.value}${this.HOTKEY_URL}`, {
            observe: 'body',
            responseType: 'json'
        });
    }

    public fetchAvailableLanguages() {
        return this.http.get<string[]>(`${this.activeUrl$.value}${this.LANGUAGES_URL}`);
    }

    public requestUiConfiguration() {
        return this.http.get<UiSettings>(`${this.activeUrl$.value}${this.UI_CONFIG_URL}`, {
            observe: 'body',
            responseType: 'json'
        });
    }

    public requestCallAlerts() {
        return this.http.get<CallAlert[]>(`${this.activeUrl$.value}${this.ALERTS_URI}`, {
            observe: 'body',
            responseType: 'json'
        });
    }

    public requestApplicationLinks() {
        return this.http.get<ExternalApplication[]>(`${this.activeUrl$.value}${this.APPLICATION_LINKS_URL}`, {
            observe: 'body',
            responseType: 'json'
        });
    }

    public requestLayoutConfiguration() {
        return this.http.get<LayoutSettings>(`${this.activeUrl$.value}${this.CONSOLE_LAYOUT_URL}`, {
            observe: 'body',
            responseType: 'json'
        });
    }

    public requestQueueConfiguration() {
        let params = new HttpParams({ encoder: new CustomHttpParamEncoder() });
        params = params.append('collection', '');
        return this.http
            .get<CallQueueConfigFormData[]>(`${this.activeUrl$.value}${this.QUEUE_CONFIG}`, {
                params: params,
                observe: 'body',
                responseType: 'json'
            })
            .pipe(map((callQueueConfigFormData) => callQueueConfigFormData.map(this.convertSkipperQueuesToCtcQueues)));
    }

    public getServiceProvidersIcons() {
        return this.http.get<ServiceResponders>(`${this.activeUrl$.value}${this.SERVICE_RESPONDERS_URL}`, {
            observe: 'body',
            responseType: 'json'
        });
    }

    public getLocationLayout() {
        return this.http.get<LocationLayoutResponse>(`${this.activeUrl$.value}${this.LOCATION_LAYOUT_URL}`, {
            observe: 'body',
            responseType: 'json'
        });
    }

    public requestPredefinedSms() {
        return this.http.get<PredefinedMessages[]>(`${this.activeUrl$.value}${this.PREDEFINED_SMS_MESSAGES}`, {
            observe: 'body',
            responseType: 'json'
        });
    }

    public requestPredefinedTdd() {
        return this.http.get<PredefinedMessages[]>(`${this.activeUrl$.value}${this.PREDEFINED_TTY_MESSAGES}`, {
            observe: 'body',
            responseType: 'json'
        });
    }

    public updateCurrentRole(role: string, sub: string) {
        return this.http.put(`${this.activeUrl$.value}${this.USER_PREFIX}/${sub}/${this.CURRENT_ROLE_SUFFIX}`, { roleName: role });
    }

    public fetchUserSelectableRoles() {
        return this.http
            .get<string[]>(`${this.activeUrl$.value}${this.USER_SELECTABLE_ROLES_ENDPOINT}`, {
                observe: 'body',
                responseType: 'json'
            })
            .pipe(map((roleNames: string[]) => roleNames.map((roleName: string) => new Role(roleName))));
    }

    public fetchSupervisedRoles() {
        return this.http
            .get<string[]>(`${this.activeUrl$.value}${this.SUPERVISED_ROLES_ENDPOINT}`, {
                observe: 'body',
                responseType: 'json'
            })
            .pipe(map((roleNames: string[]) => roleNames.map((roleName: string) => new Role(roleName))));
    }

    public fetchSupervisorAgencySettings() {
        return this.http.get<SupervisorAgencySettings>(`${this.activeUrl$.value}${this.AGENCY_SETTINGS_ENDPOINT}`);
    }

    public updateUserSettings(settings: UserSettings) {
        return this.http.post<UserSettings>(`${this.activeUrl$.value}${this.USER_SETTINGS_ENDPOINT}`, settings);
    }

    public fetchUserSettings() {
        return this.http.get<UserSettings>(`${this.activeUrl$.value}${this.USER_SETTINGS_ENDPOINT}`);
    }

    public fetchStandaloneCallColumns() {
        return this.http.get<CallListConfiguration>(`${this.activeUrl$.value}${this.CALL_LIST_ENDPOINT}`);
    }

    public fetchVersionInformation() {
    return this.http.get(`${this.activeUrl$.value}${this.VERSION_ENDPOINT}`, { responseType: 'text' });
}

    public fetchConsolePositions() {
        let params = new HttpParams({ encoder: new CustomHttpParamEncoder() });
        params = params.append('collection', '');
        return this.http.get<ConsolePosition[]>(`${this.activeUrl$.value}${this.POSITIONS_ENDPOINT}`, {
            params: params
        });
    }

    public fetchConsolePosition(position: number) {
        return this.http.get<ConsolePosition>(`${this.activeUrl$.value}${this.POSITIONS_ENDPOINT}/position-number/${position}`);
        // return this.http.get<ConsolePosition>(`${this.TEST_POSITION_CONFIG_URL}`);
    }

    public fetchSpeedDialContact(speedDials: SpeedDial[]) {
        if (!speedDials) {
            return EMPTY;
        }
        return this.http.post<Record<string, Contact>>(
            `${this.activeUrl$.value}${this.SPEED_DIAL_CONTACTS_URL}`,
            speedDials.map((speedDial) => speedDial.uuid)
        );
    }

    public fetchRingdowns(role: string) {
        return this.http.get<Ringdown[]>(`${this.activeUrl$.value}${this.RINGDOWNS_URL}?role=${role}`);
    }

    public fetchCallsDistributionPriority(roleName: string) {
        return this.http.get<any>(`${this.activeUrl$.value}${this.ROLE_BY_NAME_ENDPOINT}/${roleName}`).pipe(
            map((response) => response.abandonedDistribution)
        );
    }

    public fetchAbandonedCallsRoutingSettings() {
        return this.http.get<AbandonedCallsDistributionSettings>(`${this.activeUrl$.value}${this.ABANDONED_CALLS_CONFIGURATION_URL}`);
    }

    public fetchWallboardTemplatesWithMessages() {
        return this.http.get<WallboardTemplate[]>(`${this.activeUrl$.value}${this.FETCH_WALLBOARD_TEMPLATES_WITH_MESSAGES_URL}`);
    }

    public updateWallboardTemplatesWithMessages(messages: AcdStatusMessageContentUpdateDto[]) {
        return this.http.patch<AcdStatusMessage[]>(`${this.activeUrl$.value}${this.UPDATE_WALLBOARD_TEMPLATES_WITH_MESSAGES_URL}`, messages);
    }

    public fetchTranslationSettings() {
        return this.http.get<{ smsTranslationEnabled: boolean }>(`${this.activeUrl$.value}${this.TRANSLATION_SETTINGS_URL}`);
    }

    public requestUiLogStreamingConfiguration(position: number) {
        return this.http.get<UILogStreamingConfig>(`${this.activeUrl$.value}${this.UI_LOGGING_CONFIGS_URL}/positions/${position}`);
    }

    private convertSkipperQueuesToCtcQueues = (skipperQueue: CallQueueConfigFormData) => {
        return {
            acd: Boolean(skipperQueue.acd),
            callPriority: skipperQueue.callPriority.name,
            queueACD: skipperQueue.acd as QueueACD,
            name: skipperQueue.name,
            extension: skipperQueue.extension,
            displayName: skipperQueue.displayName,
            description: skipperQueue.description,
            maxQueueSize: skipperQueue.maxQueueSize
        } as CallQueue;
    };
}
